import { useSpecs } from '@common/graph/gem-specs';
import { ProcessDialog } from '@common/ProcessDialog';
import { useProcessDialogOpenState } from '@common/selectors';
import { CommonActionTypes } from '@common/types';
import { useCurrentGraph } from '@common/useOriginalGraph';
import { createProcess } from '@common/utils/graph/process';
import { useExecutionButton } from '@common/workflowExecutionHooks';
import { useLSPMethod } from '@LSP/base/hook';
import { LSP } from '@LSP/base/types';
import { OverlaySpinner } from '@prophecy/ui-v3/Spinner';
import { produceWithoutFreeze } from '@prophecy/utils/data';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { ExecutionSource } from 'frontend/core/src/data/execution.types';
import { useClusterStatus } from 'frontend/core/src/data/executionHooks';
import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { DatasetEntityType, NewDatasetConfig } from '../config';
import { useImportState } from '../Import/useImportState';
import { MockWorkflowContext } from './mockWorkFlowContext';

export const MockProcessDialog = () => {
  const dispatch = useDispatch();

  const mockWorkflowMethods = useContext(MockWorkflowContext);
  const gemSpecs = useSpecs();
  const { graphPath, graph, rootGraph } = useCurrentGraph();
  const { method: propertiesDidChange } = useLSPMethod(LSP.Method.propertiesDidChange);
  const { method: propertiesDidSave } = useLSPMethod(LSP.Method.propertiesDidSave);
  const {
    actions: { updateTranspilerDataset },
    transpilerState: {
      transpilerEntityTypes: { datasets }
    }
  } = useImportState();

  mockWorkflowMethods.callbackRef.current!.openDialog = usePersistentCallback((processId: string) => {
    dispatch({ type: CommonActionTypes.dialogOpen, payload: { componentId: processId } });
  });

  mockWorkflowMethods.callbackRef.current!.createGem = usePersistentCallback(() => {
    // oxlint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      //creating a new  process object
      const process = createProcess(gemSpecs, rootGraph, graph || rootGraph, {
        category: 'Source/Target',
        component: 'Source'
      });
      propertiesDidChange({
        property: graphPath,
        value: produceWithoutFreeze(rootGraph, (draft) => {
          draft.processes[process.id] = process;
        })
      });
      await propertiesDidSave();
      window.dispatchEvent(new FocusEvent('blur'));
      resolve(process);
    });
  });

  mockWorkflowMethods.callbackRef.current!.updateCurrentDataset = usePersistentCallback(function (
    dataset: NewDatasetConfig
  ) {
    let currDataset = structuredClone(mockWorkflowMethods.currentDatasetRef.current)!;
    currDataset.newDataset = {
      ...currDataset.newDataset,
      newDataset: dataset
    } as DatasetEntityType['newDataset'];
    mockWorkflowMethods.currentDatasetRef.current = currDataset;
    const datasetIndex = datasets.findIndex((d) => d.id === mockWorkflowMethods.currentDatasetRef.current!.id);
    updateTranspilerDataset(mockWorkflowMethods.currentDatasetRef.current!, datasetIndex);
    dispatch({ type: CommonActionTypes.dialogClose });
  });

  useEffect(
    function () {
      const handledatasetConfigOnMount = mockWorkflowMethods.handledatasetConfigOnMountRef;
      // mockwodkflow only gets mount if user clicks configure
      // so we need to run handleConfigClick after mounting workflow
      if (handledatasetConfigOnMount.current && rootGraph) {
        const handleDatasetConfig = mockWorkflowMethods.callbackRef.current!.handleDatasetConfig;
        const dataset = mockWorkflowMethods.currentDatasetRef.current!;
        handleDatasetConfig(dataset);
        handledatasetConfigOnMount.current = false;
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rootGraph]
  );

  const { open } = useProcessDialogOpenState();

  const clusterStatus = useClusterStatus();
  const {
    jobMetadata,
    cancel: _cancel,
    cancelling,
    inProgress
  } = useExecutionButton([ExecutionSource.inferschema], clusterStatus);

  const cancel = usePersistentCallback(_cancel);

  useEffect(
    function () {
      if (!open) {
        if (inProgress) {
          cancel(jobMetadata?.job);
          mockWorkflowMethods.callbackRef.current.disableConfigButton(true);
        } else if (!cancelling) {
          mockWorkflowMethods.callbackRef.current.disableConfigButton(false);
        }
      }
    },
    [open, jobMetadata?.job, inProgress, cancelling, cancel, mockWorkflowMethods.callbackRef]
  );

  if (!rootGraph) {
    return <OverlaySpinner tip='Loading Dataset Detail' />;
  }

  return <ProcessDialog />;
};
